import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MapLocation } from '../select-location-body/select-map-location.models';
@Component({
    selector: 'topo-address-search',
    templateUrl: './address-search.component.html',
    styleUrls: ['./address-search.component.scss'],
    standalone: false
})
export class MapAddressSearchComponent implements OnInit, OnDestroy {
  @Input() set location(value: MapLocation) {
    this._location = value;
    if (this.formField) {
      setTimeout(() => {
        this.mapSearch = this._location?.address;
        this.changeDetector.markForCheck();
      }, 10);
    }
  }
  @Input() set expandable(value: boolean) {
    this._expandable = value;
    this.expandSearch = !value;
    this.changeDetector.markForCheck();
  }
  _location: MapLocation;
  _expandable: boolean;
  @Input() formField: boolean = false;
  @Input() textarea: boolean = false;
  @Input() width: string;
  @Output() searchResultChanged = new EventEmitter<MapLocation>();

  @ViewChild('searchInput') searchInput;

  expandSearch: boolean;
  predictions: google.maps.places.AutocompletePrediction[] = [];
  autocompleteSrv: google.maps.places.AutocompleteService;
  googleGeocoder: google.maps.Geocoder;
  inputHasFocus = false;
  mapSearch: string;
  eventsAttached = false;
  linkIndex = -1;
  destroy$: Subject<void> = new Subject();

  constructor(private changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.mapSearch = null;
    this.eventsAttached = false;
  }

  private attachEvents(): void {
    if (this.searchInput?.nativeElement && !this.eventsAttached) {
      this.eventsAttached = true;
      fromEvent(this.searchInput?.nativeElement, 'input')
        .pipe(debounceTime(500))
        .pipe(takeUntil(this.destroy$))
        .subscribe(res => {
          const value = this.searchInput?.nativeElement.value;
          if (value.length === 0) {
            this.predictions = [];
            return;
          }
          this.getPredictions(value)
            .then(data => {
              this.predictions = data;
            })
            .catch(err => console.error(err));
        });
    }
  }
  ngAfterViewInit(): void {
    if (this.formField) {
      this.mapSearch = this._location?.address;
    }
    this.attachEvents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openSearch() {
    this.expandSearch = true;
    this.eventsAttached = false;
    setTimeout(() => this.attachEvents());
    setTimeout(() => this.searchInput.nativeElement.focus());
  }

  clearMapSearch() {
    this.predictions = [];
    this.expandSearch = false;
    this.changeDetector.markForCheck();
    this.mapSearch = null;
  }

  getPredictions(query: string): Promise<any> {
    const loc = !!this._location?.position ? new google.maps.LatLng(this._location?.position.lat, this._location.position.lng) : null;

    if (!!!this.autocompleteSrv) {
      this.autocompleteSrv = new google.maps.places.AutocompleteService();
    }

    const that = this;
    return new Promise((resolve, reject) => {
      this.autocompleteSrv.getPlacePredictions(
        {
          input: query,
          location: loc,
          radius: !!loc ? 48280 : null
        },
        function (predictions, status) {
          if (status == google.maps.places.PlacesServiceStatus.OK) {
            resolve(predictions);
          } else {
            reject(status);
          }

          that.changeDetector.markForCheck();
        }
      );
    });
  }

  setGeocodeResult() {
    const mapSearch = this.mapSearch;

    if (!mapSearch || mapSearch.length < 1) {
      return;
    }

    this.applyGeocodeResult(mapSearch);

    this.predictions = [];
    this.changeDetector.markForCheck();
  }

  private applyGeocodeResult(query: string) {
    if (!!!this.googleGeocoder) {
      this.googleGeocoder = new google.maps.Geocoder();
    }
    this.googleGeocoder.geocode({ address: query }, (results, status) => {
      if (status === 'OK' && results.length) {
        const sendLocation: MapLocation = {
          position: { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() },
          viewport: results[0].geometry.viewport,
          address: query
        };

        sendLocation.zoomLevel =
          results[0].types.includes('street_address') || results[0].types.includes('premise') || results[0].types.includes('subpremise')
            ? 18
            : 16;

        this.searchResultChanged.emit(sendLocation);
      } else {
      }
    });
  }

  searchPlace(place: google.maps.places.AutocompletePrediction) {
    if (place) {
      this.mapSearch = place.description;
      this.setGeocodeResult();
      setTimeout(() => this.searchInput.nativeElement.blur());
      return;
    }

    if (this.linkIndex < 0) {
      this.setGeocodeResult();
    }

    const selectedPrediction = this.predictions[this.linkIndex]?.description;

    if (!selectedPrediction) {
      this.setGeocodeResult();
      setTimeout(() => this.searchInput.nativeElement.blur());
      return;
    }
    this.mapSearch = selectedPrediction;
    this.setGeocodeResult();
    setTimeout(() => this.searchInput.nativeElement.blur());
  }

  navigateUsingKey(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowUp': // this is the ascii of arrow up
        this.linkIndex === -1 ? (this.linkIndex = 0) : this.linkIndex--;
        this.downTraverse(this.predictions.length);
        break;

      case 'ArrowDown': // this is the ascii of arrow down
        this.upTraverse(this.predictions.length);
        this.linkIndex++;
        break;

      case 'Escape': // esc key
        this.linkIndex = -1;
        break;
    }
  }

  downTraverse(listLength) {
    this.linkIndex === -1 ? (this.linkIndex = listLength - 1) : '';
  }

  upTraverse(listLength: number) {
    listLength - 1 <= this.linkIndex ? (this.linkIndex = -1) : '';
  }
}
